// Generic interface for paginated API responses.
export interface PaginatedResponse<T> {
  status: string;
  data: T[];
  pagination?: {
    current_page: number;
    page_size: number;
    total_items: number;
    total_pages: number;
  };
}

// --- From AuthContext.tsx ---
export interface ApiMenuItem {
  key: string;
  label: string;
  icon?: string;
  path?: string;
  permission?: string;
  children?: ApiMenuItem[];
}

// --- Detailed User object, based on /profile or /me endpoint ---
export interface UserRole {
  id: string;
  name: string;
  slug?: string; // Re-added as optional for compatibility
  display_name: string;
  description: string;
  is_active: boolean;
}

export interface UserPropertyAreaAssignment {
  id: string;
  property_area_id: string;
  property_area: {
    id: string;
    name: string;
    is_active: boolean;
    created_at: string;
    updated_at: string;
  };
  created_at: string;
  updated_at: string;
}

export interface User {
  id: string;
  username: string;
  email: string;
  photo_url?: string;
  full_name: string;
  phone: string;
  avatar: string;
  role_id: string;
  role: UserRole;
  is_active: boolean;
  must_change_password?: boolean;
  user_property_areas: UserPropertyAreaAssignment[];
  created_at: string;
  updated_at: string;
}

export interface LoginErrorData {
  message: string;
  data?: {
    email?: boolean;
    password?: boolean;
    remaining_attempts?: number;
  };
}

export interface AuthContextType {
  user: User | null;
  setUser: React.Dispatch<React.SetStateAction<User | null>>;
  token: string | null;
  loading: boolean;
  permissions: string[];
  hasPermission: (permissionName: string) => boolean;
  propertyAreas: any[]; // Using any to avoid circular dependency with types/property.ts
  loadingPropertyAreas: boolean;
  userMenu: ApiMenuItem[];
  refreshMenu: () => Promise<void>;
  login: (
    email: string,
    password: string,
  ) => Promise<{ success: boolean; error?: LoginErrorData }>;
  register: (userData: any) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
  isAuthenticated: boolean;
}

// --- From LeadForm.tsx ---
export interface City {
  id_kabupaten_kota: string;
  nama_kabupaten_kota: string;
  provinsi_id: string;
}

export interface Province {
  id_provinsi: string;
  nama_provinsi: string;
  kabupaten_kota_list: City[];
}

export interface UserPropertyArea {
  property_area_id: string;
}

export interface ApiUser {
  id: string;
  full_name: string;
  user_property_areas: UserPropertyArea[];
}

// --- From Monitoring/TaskLeadPage.tsx ---
export interface SalesUser {
  id: string;
  full_name: string;
}

// FCM Notification Types
export const FCMNotificationType = {
  NewLeadAssigned: "NEW_LEAD_ASSIGNED",
  SLAReminder: "SLA_REMINDER",
  SLAExpired: "SLA_EXPIRED",
  GeneralNotification: "GENERAL_NOTIFICATION",
  BroadcastNotification: "BROADCAST_NOTIFICATION",
  LeadClosingSubmitted: "LEAD_CLOSING_SUBMITTED",
  LeadClosingApproved: "LEAD_CLOSING_APPROVED",
  LeadClosingRejected: "LEAD_CLOSING_REJECTED",
} as const;

export type FCMNotificationTypeValue =
  (typeof FCMNotificationType)[keyof typeof FCMNotificationType];

// Interface for a single Notification entity.
export interface Notification {
  id: string;
  title: string;
  message: string;
  is_read: boolean;
  metadata: {
    entity_type: string;
    entity_id: string;
    fcm_type?: FCMNotificationTypeValue; // Optional field for FCM type
    closing_request_id?: string; // Optional field for lead closing request ID
    lead_id?: string; // Optional field for lead ID
  };
  created_at: string;
}

// Interface for a single Reminder/Task entity.
export interface Reminder {
  id: string;
  title: string;
  message: string;
  reminder_time: string;
  lead: {
    id: string;
    full_name: string;
  };
}

export * from "./leads";
export * from "./dashboard";
export * from "./leaderboard";
export * from "./whatsapp";
export * from "./whatsapp-broadcast";
